import React from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { AUTH_COLORS, AUTH_FONT } from "../../config/authConstants";
import AdminIcon from "./adminIcons";

export var ADMIN_COLORS = {
  primary: AUTH_COLORS.primary,
  primaryHover: AUTH_COLORS.primaryHover,
  primarySoft: AUTH_COLORS.bgSoft,
  navy: "#050A44",
  navyHover: "#141B66",
  navySoft: "#ECEDF3",
  text: AUTH_COLORS.text,
  textMuted: "#5B6570",
  textSubtle: AUTH_COLORS.textSecondary,
  border: AUTH_COLORS.border,
  borderSoft: AUTH_COLORS.borderSecondary,
  surface: "#FFFFFF",
  page: "#F1F2F4",
  error: AUTH_COLORS.error,
  errorSoft: AUTH_COLORS.errorBg,
  success: "#15803D",
  successSoft: "#DCFCE7",
  warning: "#B45309",
  warningSoft: "#FEF3C7",
  violet: "#6D28D9",
  violetSoft: "#F3E8FF",
  neutral: "#4B5563",
  neutralSoft: "#F3F4F6",
  // HR Admin Figma tokens
  brand: "#0011D3",
  brandHover: "#000FB8",
  brandSoft: "#E7F1FF",
  tabActive: "#D3E6FF",
  card: "#FDFDFD",
  line: "#C7CCD1",
  lineSoft: "#E6E8EA",
  label: "#171A1C",
  labelMuted: "#848F9A",
  labelSecondary: "#5C6670",
  link: "#3F85F5",
  required: "#D10011",
  fieldError: "#FF2638",
  pageText: "#1C1B1C",
};

export var ADMIN_FONT = AUTH_FONT;
export var ADMIN_INTER_FONT = '"Inter", ' + AUTH_FONT;

export var ADMIN_HEADER_HEIGHT = 64;

/**
 * Admin type scale, sized above the Figma values for readability.
 * Change sizes here rather than in individual screens.
 */
export var ADMIN_TYPE = {
  appName: { fontSize: 20, lineHeight: "28px", fontWeight: 400 },
  userName: { fontSize: 16, lineHeight: "22px", fontWeight: 400 },
  pageTitle: { fontSize: 24, lineHeight: "34px", fontWeight: 500 },
  button: { fontSize: 16, lineHeight: "22px", fontWeight: 400 },
  body: { fontSize: 15, lineHeight: "20px", fontWeight: 400 },
  statLabel: { fontSize: 15, lineHeight: "20px", fontWeight: 500 },
  statValue: { fontSize: 28, lineHeight: "34px", fontWeight: 500 },
  tableHead: { fontSize: 15, lineHeight: "20px", fontWeight: 600 },
  tableCell: { fontSize: 16, lineHeight: "24px", fontWeight: 400 },
  chip: { fontSize: 14, lineHeight: "20px", fontWeight: 500 },
  sectionTitle: { fontSize: 18, lineHeight: "26px", fontWeight: 600 },
  profileName: { fontSize: 22, lineHeight: "30px", fontWeight: 600 },
  recordName: { fontSize: 20, lineHeight: "28px", fontWeight: 600 },
  fieldLabel: { fontSize: 14, lineHeight: "20px", fontWeight: 500 },
  fieldValue: { fontSize: 16, lineHeight: "24px", fontWeight: 500 },
  dialogTitle: { fontSize: 22, lineHeight: "30px", fontWeight: 500 },
  inputLabel: { fontSize: 15, lineHeight: "20px", fontWeight: 500 },
  helper: { fontSize: 13, lineHeight: "18px", fontWeight: 400 },
};

/** Icon sizes paired with ADMIN_TYPE. */
export var ADMIN_ICON_SIZE = {
  button: 18,
  action: 20,
  stat: 28,
};

export var ADMIN_CONTROL_HEIGHT = 40;

/** Figma buttons: 6px radius, #0011D3 primary — shared by HR and Super Admin. */
export var ADMIN_BUTTON_SX = {
  contained: {
    bgcolor: ADMIN_COLORS.brand,
    color: "#FFFFFF",
    fontFamily: ADMIN_FONT,
    fontSize: ADMIN_TYPE.button.fontSize,
    fontWeight: 400,
    lineHeight: ADMIN_TYPE.button.lineHeight,
    px: "12px",
    height: ADMIN_CONTROL_HEIGHT,
    minWidth: 0,
    borderRadius: "6px",
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: ADMIN_COLORS.brandHover, boxShadow: "none" },
    "&.Mui-disabled": { bgcolor: "#9AA2F0", color: "#FFFFFF" },
  },
  outlined: {
    color: ADMIN_COLORS.label,
    border: "1px solid " + ADMIN_COLORS.line,
    bgcolor: ADMIN_COLORS.card,
    fontFamily: ADMIN_FONT,
    fontSize: ADMIN_TYPE.button.fontSize,
    fontWeight: 400,
    lineHeight: ADMIN_TYPE.button.lineHeight,
    px: "12px",
    height: ADMIN_CONTROL_HEIGHT,
    minWidth: 0,
    borderRadius: "6px",
    whiteSpace: "nowrap",
    "&:hover": { borderColor: ADMIN_COLORS.brand, bgcolor: ADMIN_COLORS.brandSoft },
    "&.Mui-disabled": { color: "#9AA0AC", borderColor: ADMIN_COLORS.lineSoft },
  },
  /** Figma "icon + label" padding (6px 12px 6px 10px, 6px gap); combine via an sx array. */
  withStartIcon: {
    pl: "10px",
    pr: "12px",
    "& .MuiButton-startIcon": { mr: "6px", ml: 0 },
  },
};

/** Brand-blue switch used for every Login toggle. */
export var ADMIN_SWITCH_SX = {
  width: 44,
  height: 24,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: "2px",
    color: "#FFFFFF",
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#FFFFFF",
      "& + .MuiSwitch-track": { bgcolor: ADMIN_COLORS.brand, opacity: 1 },
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
  },
  "& .MuiSwitch-thumb": { width: 20, height: 20, boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: "12px", bgcolor: ADMIN_COLORS.line, opacity: 1 },
};

/** Per-form status → label + Figma colours (drawer, review page). */
export var FORM_STATUS_META = {
  notStarted: { label: "Awaiting Action", color: "#FF5967", bg: "#FFF0F1" },
  draft: { label: "In Progress", color: "#00A49B", bg: "#EEFFFB" },
  awaitingReview: { label: "Needs Review", color: "#833AFF", bg: "#F3F0FF" },
  resubmission: { label: "Changes Requested", color: "#FF8F07", bg: "#FFF9EC" },
  approved: { label: "Approved", color: "#09B500", bg: "#EAFFE4" },
};

export function formStatusBucket(status) {
  if (status === "review done") return "approved";
  if (status === "submitted" || status === "resubmitted") {
    return "awaitingReview";
  }
  if (status === "resubmission required") return "resubmission";
  if (status === "draft") return "draft";
  return "notStarted";
}

/**
 * Pastel pill; `meta` overrides the per-form bucket (e.g. overall joinee status).
 * `size="large"` is for list tables; the default compact size fits the 124px drawer column.
 */
export function StatusChip(props) {
  var large = props.size === "large";
  var meta =
    props.meta ||
    FORM_STATUS_META[props.bucket || formStatusBucket(props.status)] ||
    FORM_STATUS_META.notStarted;
  return (
    <Box
      component="span"
      sx={Object.assign(
        {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: large ? 28 : 22,
          px: large ? "14px" : "12px",
          py: "2px",
          boxSizing: "border-box",
          borderRadius: "1000px",
          border: props.borderless ? "none" : "1px solid " + meta.color,
          bgcolor: meta.bg,
          color: meta.color,
          fontFamily: ADMIN_FONT,
          fontSize: large ? ADMIN_TYPE.chip.fontSize : 12,
          fontWeight: large ? ADMIN_TYPE.chip.fontWeight : 400,
          lineHeight: large ? ADMIN_TYPE.chip.lineHeight : "normal",
          whiteSpace: "nowrap",
        },
        props.sx || {}
      )}
    >
      {props.label || meta.label}
    </Box>
  );
}

/** Grey page area under the app header. */
export function AdminPage(props) {
  return (
    <Box
      component="main"
      sx={Object.assign(
        {
          bgcolor: ADMIN_COLORS.page,
          flex: 1,
          minHeight: 0,
          p: "14px",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        },
        props.sx || {}
      )}
    >
      {props.children}
    </Box>
  );
}

/** Desktop: sheet fills the viewport under the app header (+ page padding) so tables scroll inside it. */
export var ADMIN_VIEWPORT_SHEET_SX = {
  height: { md: "calc(100vh - " + (ADMIN_HEADER_HEIGHT + 28) + "px)" },
  minHeight: { md: 560 },
};

/** White rounded sheet that holds a whole screen (list, record page). */
export function AdminSheet(props) {
  return (
    <Box
      sx={Object.assign(
        {
          bgcolor: ADMIN_COLORS.card,
          border: "1px solid " + ADMIN_COLORS.line,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          flex: "1 0 auto",
          overflow: "hidden",
        },
        props.viewport ? ADMIN_VIEWPORT_SHEET_SX : {},
        props.sx || {}
      )}
    >
      {props.children}
    </Box>
  );
}

/** Sheet title row: optional back caret, 20px title, right-aligned actions, divider. */
export function AdminSheetHeader(props) {
  return (
    <Box sx={{ px: "20px", pt: "14px", flexShrink: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: "10px",
          minHeight: ADMIN_CONTROL_HEIGHT,
          pb: "12px",
          borderBottom: "1px solid " + ADMIN_COLORS.lineSoft,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
          {props.onBack ? (
            <IconButton
              aria-label={props.backLabel || "Back"}
              onClick={props.onBack}
              sx={{ p: 0, width: 30, height: 30, color: ADMIN_COLORS.label }}
            >
              <AdminIcon name="CaretRight" size={28} sx={{ transform: "rotate(180deg)" }} />
            </IconButton>
          ) : null}
          <Typography
            component="h1"
            sx={{
              fontFamily: ADMIN_FONT,
              fontSize: ADMIN_TYPE.pageTitle.fontSize,
              fontWeight: ADMIN_TYPE.pageTitle.fontWeight,
              lineHeight: ADMIN_TYPE.pageTitle.lineHeight,
              color: ADMIN_COLORS.label,
              whiteSpace: "nowrap",
            }}
          >
            {props.title}
          </Typography>
        </Box>
        {props.action ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              gap: "12px",
              minWidth: 0,
            }}
          >
            {props.action}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export function AdminPageHeader(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: ADMIN_FONT,
            fontSize: 20,
            fontWeight: 500,
            color: ADMIN_COLORS.label,
            lineHeight: 1.3,
          }}
        >
          {props.title}
        </Typography>
        {props.subtitle ? (
          <Typography
            sx={{
              fontFamily: ADMIN_FONT,
              fontSize: 14,
              color: ADMIN_COLORS.labelMuted,
              mt: 0.5,
            }}
          >
            {props.subtitle}
          </Typography>
        ) : null}
      </Box>
      {props.action ? <Box sx={{ flexShrink: 0 }}>{props.action}</Box> : null}
    </Box>
  );
}

export function AdminCard(props) {
  return (
    <Paper
      elevation={0}
      sx={Object.assign(
        {
          bgcolor: ADMIN_COLORS.card,
          border: "1px solid " + ADMIN_COLORS.line,
          borderRadius: "12px",
          p: { xs: 2, md: 2.5 },
          boxShadow: "none",
        },
        props.sx || {}
      )}
    >
      {props.title ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: ADMIN_FONT,
                fontSize: 16,
                fontWeight: 500,
                color: ADMIN_COLORS.label,
              }}
            >
              {props.title}
            </Typography>
            {props.subtitle ? (
              <Typography
                sx={{
                  fontFamily: ADMIN_FONT,
                  fontSize: 12,
                  color: ADMIN_COLORS.labelMuted,
                  mt: 0.25,
                }}
              >
                {props.subtitle}
              </Typography>
            ) : null}
          </Box>
          {props.action || null}
        </Box>
      ) : null}
      {props.children}
    </Paper>
  );
}

/** "2026-04-01" → "01 Apr 2026"; tolerant of ISO strings and empty values. */
export function formatDisplayDate(value) {
  if (!value) return "—";
  var raw = String(value).trim();
  var match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  var date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(raw);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** "2026-04-01" → "01/04/2026" (Figma key-information format). */
export function formatSlashDate(value) {
  if (!value) return "—";
  var date = dayjs(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : "—";
}

export function initialsOf(name) {
  var parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  var first = parts[0].charAt(0);
  var last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (first + last).toUpperCase();
}
